/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type Maybe from 'graphql/tsutils/Maybe';

import Imgix from 'js/components/Imgix';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import type { CtaFragment, ImageFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

import _t from 'i18n!nls/ent-website';

const styles = {
  cnt: (_: Theme, backgroundColor?: Maybe<string>, fontColor?: Maybe<string>) => css`
    border-radius: 8px;
    overflow: hidden;
    background: ${backgroundColor ?? 'inherit'};
    margin: var(--cds-spacing-400) var(--cds-spacing-150) var(--cds-spacing-400) 0;
    width: 100%;
    display: flex;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    display: flex;

    h1,
    h2,
    h3,
    p,
    ul,
    li {
      color: ${fontColor ?? 'inherit'};
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  logo: css`
    object-fit: contain;
    width: 200px;
    object-position: top left;
    margin-bottom: var(--cds-spacing-200);
  `,
  headline: css`
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.72px;
    margin-bottom: 8px;
  `,
  body: (_: Theme, type?: Maybe<string>) => css`
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.48px;
    margin-bottom: ${type === 'inline' ? '0' : '32px'};

    ${breakpoints.down('sm')} {
      margin-bottom: 32px;
    }
  `,
  cta: (_: Theme, type?: Maybe<string>) => css`
    flex: 1;
    display: flex;
    min-width: 220px;
    height: fit-content;
    align-self: ${type === 'inline' ? 'auto' : 'center'};
    margin-left: ${type === 'inline' ? 'var(--cds-spacing-200)' : '0'};

    ${breakpoints.down('sm')} {
      margin-left: 0;
    }
  `,
  desktopImage: css`
    width: 200px;
    height: auto;
    position: relative;

    img {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      object-fit: cover;
    }

    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  mobileImage: (_: Theme, type?: Maybe<string>) => css`
    width: 100%;

    img {
      height: 100%;
      object-fit: cover;
    }

    ${breakpoints.up('sm')} {
      display: ${type === 'inline' ? 'none' : 'block'};
    }
  `,
};

export type Props = {
  type?: Maybe<string>;
  headline?: Maybe<string>;
  body?: Maybe<string>;
  cta?: Maybe<CtaFragment>;
  logo?: Maybe<ImageFragment>;
  image?: Maybe<ImageFragment>;
  hideImageOnMobile?: Maybe<boolean>;
  backgroundColor?: Maybe<string>;
  fontColor?: Maybe<string>;
  module: BlockLevelModule;
};

const getPromoUnitStylesForType = (type?: Maybe<string>) => {
  const sharedFullWidthButtonStyle = css`
    a {
      width: 100%;
      max-width: 100%;
    }
  `;

  const sharedFloatStyles = css`
    width: 320px;
    flex-direction: column;

    ${breakpoints.down('xs')} {
      width: 100%;
      margin: var(--cds-spacing-300) 0;
    }
  `;

  const inlineCss = css`
    width: 100%;
    flex-direction: row !important;
    flex-wrap: nowrap;

    ${breakpoints.down('xs')} {
      flex-direction: column !important;
      ${sharedFullWidthButtonStyle}
    }
  `;

  switch (type) {
    case 'inline':
      return inlineCss;
    case 'float-left':
      return css`
        float: left;
        margin: var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-300) 0;

        ${sharedFullWidthButtonStyle}
        ${sharedFloatStyles}
      `;
    case 'float-right':
      return css`
        float: right;
        margin: var(--cds-spacing-300) 0 var(--cds-spacing-300) var(--cds-spacing-300);

        ${sharedFullWidthButtonStyle}
        ${sharedFloatStyles}
      `;
    default:
      return inlineCss;
  }
};

const EnterpriseArticleInlinePromoUnit = ({
  type,
  headline,
  body,
  cta,
  logo,
  fontColor,
  backgroundColor,
  image,
  hideImageOnMobile,
  module,
}: Props) => {
  const theme = useTheme();

  return (
    <div css={[styles.cnt(theme, backgroundColor, fontColor), getPromoUnitStylesForType(type)]}>
      {/* Desktop Image */}
      {image?.url && type === 'inline' && (
        <div css={[styles.desktopImage]}>
          <Imgix src={image?.url} alt={image?.description ?? _t('Promo Unit Image')} />
        </div>
      )}
      <Grid
        container
        wrap={type === 'inline' ? 'nowrap' : undefined}
        alignItems={type === 'inline' ? 'center' : undefined}
        justifyContent={type === 'inline' ? 'center' : undefined}
        direction={type === 'inline' ? 'row' : undefined}
        css={css`
          padding: var(--cds-spacing-300);

          ${breakpoints.down('sm')} {
            flex-direction: column;
          }
        `}
      >
        <Grid container direction="column">
          {logo?.url && <Imgix src={logo?.url} alt={logo?.description ?? _t('Coursera Logo')} css={styles.logo} />}
          <Typography2 component="h3" variant="subtitleMedium" css={styles.headline}>
            {headline}
          </Typography2>
          <Markdown css={styles.body(theme, type)} source={body} module={module} />
        </Grid>
        {cta?.ctaTarget && cta?.ctaText && (
          <ContentfulButton
            component="a"
            href={cta?.ctaTarget}
            position={0}
            module={module}
            target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
            variant={cta?.invert ? 'primaryInvert' : 'primary'}
            styles={styles.cta(theme, type)}
            role="button"
          >
            {cta.ctaText}
          </ContentfulButton>
        )}
      </Grid>
      {/* Mobile/Float Image */}
      {!hideImageOnMobile && image?.url && (
        <div css={styles.mobileImage(theme, type)}>
          <Imgix src={image?.url} alt={image?.description ?? _t('Promo Unit Image')} />
        </div>
      )}
    </div>
  );
};

export default EnterpriseArticleInlinePromoUnit;
